/**
 * Draft Editor Component
 *
 * Read-only display of draft rule fields with action buttons.
 * Users make edits via the AI chat.
 * Styled to match JotAudit design patterns.
 */

import React, { useCallback, useState } from 'react';
import {
  AutoAwesome as AiIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type { DraftRuleResponse } from '@store/generated/rule-build';

import { DRAFT_STATUS_LABELS } from '../constants';
import { useDeleteDraftMutation, useSaveDraftMutation } from '../store';
import type { DraftStatus } from '../types';

import { DraftMarkdown } from './DraftEditor.styles';

export interface DraftEditorProps {
  draft: DraftRuleResponse;
  taskId: string;
  /** Callback to refetch task status after mutations */
  onRefetch?: () => void;
}

/**
 * Get status display info
 */
const getStatusDisplay = (status: string) => {
  switch (status) {
    case 'ready':
      return { color: '#22C55E', bgColor: '#22C55E15', label: 'Ready' };
    case 'saved':
      return { color: '#2196F3', bgColor: '#2196F315', label: 'Saved' };
    default:
      return { color: '#6B7280', bgColor: '#6B728015', label: 'Draft' };
  }
};

export const DraftEditor: React.FC<DraftEditorProps> = ({ draft, taskId, onRefetch }) => {
  const theme = useTheme();
  const { ruleBuild } = useEventTracking();

  // Mutations
  const [deleteDraft, { isLoading: isDeleting }] = useDeleteDraftMutation();
  const [saveDraft, { isLoading: isSaving }] = useSaveDraftMutation();

  // Local state
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check if draft can be saved (has title + body, not already saved or generating)
  const canSave = Boolean(
    draft.title &&
    draft.body_md &&
    (draft.status === 'draft' || draft.status === 'ready') &&
    !isSaving,
  );

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      setError(null);
      setSuccessMessage(null);
      const result = await saveDraft({ taskId, draftId: draft.id }).unwrap();

      // Track successful save
      ruleBuild.draftSaved(taskId, draft.id, {
        is_update: result.is_update,
        already_saved: result.already_saved,
      });

      if (result.already_saved) {
        setSuccessMessage('This rule has already been saved');
      } else if (result.is_update) {
        setSuccessMessage('Rule updated successfully');
      } else {
        setSuccessMessage('Rule saved to library');
      }
      onRefetch?.();
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'data' in err
          ? (err.data as { detail?: string })?.detail || 'Failed to save draft'
          : 'Failed to save draft';
      setError(errorMessage);
      ruleBuild.error(taskId, {
        error_type: 'draft_save_failed',
        error_message: errorMessage,
      });
      console.error('Failed to save draft:', err);
    }
  }, [saveDraft, taskId, draft.id, onRefetch, ruleBuild]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete this draft?')) {
      return;
    }
    try {
      await deleteDraft({ taskId, draftId: draft.id }).unwrap();
      ruleBuild.draftDeleted(taskId, draft.id);
      onRefetch?.();
    } catch (err) {
      setError('Failed to delete draft');
      ruleBuild.error(taskId, {
        error_type: 'draft_delete_failed',
      });
      console.error('Failed to delete draft:', err);
    }
  }, [deleteDraft, taskId, draft.id, onRefetch, ruleBuild]);

  const isLoading = isDeleting || isSaving;
  const statusDisplay = getStatusDisplay(draft.status);

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'textColors.darkText',
            }}
          >
            Draft Rule
          </Typography>

          {/* Status badge */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: statusDisplay.bgColor,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: statusDisplay.color,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
              }}
            >
              {DRAFT_STATUS_LABELS[draft.status as DraftStatus] || draft.status}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Save button - only show if saveable */}
          {draft.status !== 'saved' && (
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!canSave}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}

          {/* Saved indicator */}
          {draft.status === 'saved' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: '20px',
                bgcolor: '#22C55E15',
                color: '#22C55E',
              }}
            >
              <CheckIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                Saved
              </Typography>
            </Box>
          )}

          {/* Delete button */}
          <Tooltip title="Delete draft">
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={isLoading || draft.status === 'saved'}
              sx={{
                color: 'textColors.lightText',
                '&:hover': {
                  bgcolor: theme.palette.grey[100],
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Success alert */}
      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage(null)}
          sx={{ mx: 3, mt: 2, borderRadius: 2, flexShrink: 0 }}
        >
          {successMessage}
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mx: 3, mt: 2, borderRadius: 2, flexShrink: 0 }}
        >
          {error}
        </Alert>
      )}

      {/* Edit hint */}
      <Box
        sx={{
          mx: 3,
          mt: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: `${theme.palette.primary.main}08`,
          border: `1px solid ${theme.palette.primary.main}20`,
          borderRadius: 2,
          flexShrink: 0,
        }}
      >
        <AiIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
        <Typography variant="body2" sx={{ color: 'textColors.darkText' }}>
          Use the chat to make changes to this rule
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, minHeight: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Title */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: draft.title ? 'textColors.darkText' : 'textColors.lightText',
                fontStyle: draft.title ? 'normal' : 'italic',
              }}
            >
              {draft.title || 'Untitled Rule'}
            </Typography>
          </Box>

          {/* Rule Content - Rendered as Markdown */}
          {draft.body_md ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
              }}
            >
              <DraftMarkdown markdown={draft.body_md} />
            </Paper>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: 'textColors.lightText',
                fontStyle: 'italic',
              }}
            >
              No content yet
            </Typography>
          )}

          {/* Metadata section */}
          {(draft.cpt_codes?.length || draft.provider_types?.length || draft.payers?.length) && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'textColors.darkText',
                  mb: 2,
                }}
              >
                Metadata
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* CPT Codes */}
                {draft.cpt_codes && draft.cpt_codes.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'textColors.lightText',
                        fontWeight: 600,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      CPT Codes
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {draft.cpt_codes.map(code => (
                        <Chip
                          key={code}
                          label={code}
                          size="small"
                          sx={{
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.grey[300]}`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Provider Types */}
                {draft.provider_types && draft.provider_types.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'textColors.lightText',
                        fontWeight: 600,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Provider Types
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {draft.provider_types.map(type => (
                        <Chip
                          key={type}
                          label={type}
                          size="small"
                          sx={{
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.grey[300]}`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Payers */}
                {draft.payers && draft.payers.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'textColors.lightText',
                        fontWeight: 600,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Payers
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {draft.payers.map(payer => (
                        <Chip
                          key={payer}
                          label={payer}
                          size="small"
                          sx={{
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.grey[300]}`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* Source info */}
          {draft.source && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pt: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: 'textColors.lightText' }}>
                Source: {draft.source.type}
                {draft.source.document_name && ` · ${draft.source.document_name}`}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default DraftEditor;
